import TR from 'utils/TR';
import T from 'utils/T';
import ex from 'utils/ex';
import HBox from 'components/common/HBox';
import Section from 'components/common/Section';
import SortButton from '../../../common/SortButton';
import DIRECTION_VERTICAL from 'constants/DIRECTION_VERTICAL';
import useSort from 'hooks/tables/useSort';
import PagingIndicator from 'components/common/paging/PagingIndicator';
import usePaging from 'hooks/tables/usePaging';
import defaultRenderPrevButtons from 'components/common/paging/defaultRenderPrevButtons';
import defaultRenderNextButtons from 'components/common/paging/defaultRenderNextButtons';

import useReportAbnormalBloodSugar from 'hooks/communication/useReportAbnormalBloodSugar';
import React from 'react';
import Recoil from 'recoil';
import * as AppStore from 'stores/AppStore';
import SORT_DEFAULT from 'constants/SORT_DEFAULT';
import SORT_DESCENDING from 'constants/SORT_DESCENDING';
import useEffectOnce from 'hooks/common/useEffectOnce';
import SelectButton from 'components/common/SelectButton';
import DialogButton from 'components/common/DialogButton';
import BloodGlocoseDialog from '../../../dialogs/BloodGlocoseDialog';
import * as Luxon from 'luxon';
import { numberWithComma } from 'utils/number';
import CHECK_COLORS from 'constants/CHECK_COLORS';

window.NB_CHANGED_LOG = +(window.localStorage.getItem('NB_CHANGED_LOG') || 0);;
const DEFAULT_SORT_LIST = [{ sort_by: 'occur_tm', order_by: SORT_DESCENDING }];
const PAGE_ROW_CNT = 5
export default function GlocoseLowList(props) {
  const {
    category,
    duration,
    ...rest
  } = props;

  const [sort_list, setSortList] = React.useState(() => DEFAULT_SORT_LIST)

  const {
    order: dateOrder,
    onOrderChange: onDateOrderChange,
  } = useSort(false, SORT_DESCENDING);
  const {
    order: timeOrder,
    onOrderChange: onTimeOrderChange,
  } = useSort();
  const {
    order: durationOrder,
    onOrderChange: onDurationOrderChange,
  } = useSort();
  const {
    order: hyperglycemiaOrder,
    onOrderChange: onHyperglycemiaOrderChange,
  } = useSort();

  React.useEffect(() => {
    if (window.NB_CHANGED_LOG) console.log('GlocoseLowList.occur_tm', dateOrder);
    const additionals = dateOrder === SORT_DEFAULT ? [] : [{ sort_by: 'occur_tm', order_by: dateOrder }];
    setSortList(list => [...additionals, ...list.filter(({ sort_by }) => sort_by !== 'occur_tm')])
  }, [dateOrder]);

  React.useEffect(() => {
    if (window.NB_CHANGED_LOG) console.log('GlocoseLowList.tmzone_cd', timeOrder);
    const additionals = timeOrder === SORT_DEFAULT ? [] : [{ sort_by: 'tmzone_cd', order_by: timeOrder }];
    setSortList(list => [...additionals, ...list.filter(({ sort_by }) => sort_by !== 'tmzone_cd')])
  }, [timeOrder]);

  React.useEffect(() => {
    if (window.NB_CHANGED_LOG) console.log('GlocoseLowList.duration', durationOrder);
    const additionals = durationOrder === SORT_DEFAULT ? [] : [{ sort_by: 'duration', order_by: durationOrder }];
    setSortList(list => [...additionals, ...list.filter(({ sort_by }) => sort_by !== 'duration')])
  }, [durationOrder]);

  React.useEffect(() => {
    if (window.NB_CHANGED_LOG) console.log('GlocoseLowList.min_bldsugr', hyperglycemiaOrder);
    const additionals = hyperglycemiaOrder === SORT_DEFAULT ? [] : [{ sort_by: 'min_bldsugr', order_by: hyperglycemiaOrder }];
    setSortList(list => [...additionals, ...list.filter(({ sort_by }) => sort_by !== 'min_bldsugr')])
  }, [hyperglycemiaOrder]);

  const {
    requestReportAbnormalBloodSugar,
  } = useReportAbnormalBloodSugar();

  const [total, setTotal] = React.useState(0);
  const [list, setList] = React.useState([]);
  const period_start = Recoil.useRecoilValue(AppStore.assets.period_start);
  const period_end = Recoil.useRecoilValue(AppStore.assets.period_end);

  const {
    page,
    onPage,
    npage,
    onNPage,
    maxPage,
    onMaxPage
  } = usePaging({
    resetCondition: [period_start, period_end],
    defaultMaxPage: 1,
  });

  const [periodUpdated, setPeriodUpdated] = React.useState(0);
  React.useEffect(() => {
    setPeriodUpdated(u => u + 1)
  }, [period_start, period_end]);

  const [bld_sugr_at_search, setBloodGlocoseList] = React.useState(70);
  const onBloodSugarChange = React.useCallback((e) => {
    setBloodGlocoseList(() => e.target.value);
    onPage(1);
  }, [onPage]);

  const setGlocoseLowList = Recoil.useSetRecoilState(AppStore.assets.data('GlocoseLowList'));

  const patient_no = Recoil.useRecoilValue(AppStore.assets.patient_no);
  React.useEffect(() => {
    if (!patient_no) return;
    requestReportAbnormalBloodSugar({
      patient_no,
      search_start_time: period_start,
      search_end_time: period_end,
      abnormal_bld_sugr_tp_cd: 'L',
      bld_sugr_at_search,
      page_row_cnt: PAGE_ROW_CNT,
      page_no: npage,
      sort_list,
    }, (success, data) => {
      if (success) {
        setList(() => data.abnormal_bld_sugr_list);
        if (!duration) {
          setGlocoseLowList(() => ({ list: data.abnormal_bld_sugr_list }));
        }
        setTotal(() => data.total_row_cnt);
        onPage(data.page_no);
        onMaxPage(data.max_page_no || 1);
      } else {
        setTotal(() => 0);
        setList(() => []);
        setGlocoseLowList(() => ({ list: [] }));
        onMaxPage(1);
        onPage(1);
        onNPage(1);
      }
    });
  }, [periodUpdated, sort_list, patient_no, bld_sugr_at_search, requestReportAbnormalBloodSugar, period_start, period_end, npage, onMaxPage, onPage, onNPage, setGlocoseLowList, duration]);


  const rendered = React.useMemo(() => {
    return (
      <Section
        {...ex('GlocoseLowList', rest)}
        {...{
          caption: T('저혈당 발생'),
          options: (
            <>
              <HBox full>
                {T('| 총 {0}건', total)}
              </HBox>
              <div>
                <SelectButton
                  {...props}
                  {...{
                    category: 'App',
                    onChange: onBloodSugarChange,
                    value: +bld_sugr_at_search,
                    defaultValue: 70,
                    options: [{
                      value: 70,
                      label: '70 mg/dL 미만',
                    }, {
                      value: 54,
                      label: '54 mg/dL 미만',
                    }],
                  }}
                />
              </div>
            </>
          ),
          direction: DIRECTION_VERTICAL,
        }}
      >
        <table>
          <thead>
            <tr>
              <th>
                <div>
                  {T('No.')}
                </div>
              </th>
              <th>
                <div>
                  <div>
                    {T('날짜')}
                  </div>
                  <div>
                    <SortButton {...{ order: dateOrder, onClick: onDateOrderChange }} />
                  </div>
                </div>
              </th>
              <th>
                <div>
                  {T('시간대')}
                  <SortButton {...{ order: timeOrder, onClick: onTimeOrderChange }} />
                </div>
              </th>
              <th>
                <div>
                  {T('지속시간(분)')}
                  <SortButton {...{ order: durationOrder, onClick: onDurationOrderChange }} />
                </div>
              </th>
              <th>
                <div>
                  {T('최저혈당')}
                  <SortButton {...{ order: hyperglycemiaOrder, onClick: onHyperglycemiaOrderChange }} />
                </div>
              </th>
              <th>
                <div>
                  {T('식사')}
                </div>
              </th>
              <th>
                <div>
                  {T('2시간내 활동(보)')}
                </div>
              </th>
              <th>
                <div>
                  {T('2시간내 볼루스(U)')}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {list.map((item, index) => {
              const {
                occur_start_time,
                occur_end_time,
                occur_time_name,
                duration,
                highest_bld_sugr,
                '2_hr_within_insulin_amt': insulin_amt,
                '2_hr_within_main_food': main_food,
                '2_hr_within_step_cnt': step_cnt,
              } = item;
              const start_time = !occur_start_time ? '' : Luxon.DateTime.fromFormat(occur_start_time, 'yyyyMMddHHmmss').toFormat('yy.MM.dd HH:mm');
              const end_time = !occur_end_time ? '' : Luxon.DateTime.fromFormat(occur_end_time, 'yyyyMMddHHmmss').toFormat('HH:mm');
              return (
                <tr key={index}>
                  <td>
                    <div>
                      {index + (page - 1) * PAGE_ROW_CNT + 1}
                    </div>
                  </td>
                  <td>
                    <div>
                      <DialogButton
                        {...{ category: 'App' }}
                        {...ex('BloodGlocoseDialogButton')}
                        {...{ label: T('{0} ~ {1}', start_time || '', end_time || '') }}
                      >
                        <BloodGlocoseDialog
                          {...{
                            start_time: occur_start_time,
                            end_time: occur_end_time,
                            type: 'min',
                          }}
                        />
                      </DialogButton>
                    </div>
                  </td>
                  <td>
                    <div>
                      {occur_time_name || ''}
                    </div>
                  </td>
                  <td>
                    <div>
                      {numberWithComma(duration || '')}
                    </div>
                  </td>
                  <td>
                    <div {...ex(['BASE_COLOR', CHECK_COLORS('B', highest_bld_sugr)])}>
                      {highest_bld_sugr || 0}
                    </div>
                  </td>
                  <td>
                    <div>
                      {main_food || ''}
                    </div>
                  </td>
                  <td>
                    <div>
                      {numberWithComma(step_cnt || 0)}
                    </div>
                  </td>
                  <td>
                    <div>
                      {insulin_amt || 0}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        <PagingIndicator
          {...{
            indicatorCount: 5,
            maxPage,
            page,
            onNPage,
            renderPrevButtons: defaultRenderPrevButtons,
            renderNextButtons: defaultRenderNextButtons,
          }}
        />
      </Section>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, dateOrder, timeOrder, hyperglycemiaOrder, bld_sugr_at_search]);
  return rendered;
}
